import React, { useEffect, useMemo, useState } from "react";
import { api, PRIORITY_CODES } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus, Trash, DownloadSimple, UploadSimple, PencilSimple,
  ArrowUp, ArrowDown, Copy, CopySimple, Warning, ClockClockwise, FileCsv, Sparkle, ClockCounterClockwise, DotsSixVertical,
} from "@phosphor-icons/react";
import { tryParseCompound } from "@/lib/compoundParser";
import VersionHistoryDialog from "@/components/admin/VersionHistoryDialog";
import DiscriminatorSearchDialog from "@/components/admin/DiscriminatorSearchDialog";
import CopyDiscriminatorDialog from "@/components/admin/CopyDiscriminatorDialog";
import { MagnifyingGlass } from "@phosphor-icons/react";

// --- Config ---------------------------------------------------------------
const ACTIVATION_TYPES = [
  { v: "manual", l: "Selezione manuale dell'infermiere" },
  { v: "vital", l: "Parametro vitale" },
  { v: "scale", l: "Scala clinica" },
  { v: "lab", l: "Valore di laboratorio" },
  { v: "combined", l: "Combinazione di condizioni" },
];

const PARAMETERS = [
  { v: "pa_sys", l: "PA sistolica", unit: "mmHg" },
  { v: "pa_dia", l: "PA diastolica", unit: "mmHg" },
  { v: "fc", l: "FC", unit: "bpm" },
  { v: "fr", l: "FR", unit: "/min" },
  { v: "spo2", l: "SpO₂", unit: "%" },
  { v: "temp", l: "Temperatura", unit: "°C" },
  { v: "glicemia", l: "Glicemia", unit: "mg/dL" },
  { v: "gcs", l: "GCS", unit: "punti" },
  { v: "nrs", l: "Dolore NRS", unit: "0–10" },
  { v: "cpss", l: "CPSS (segni positivi)", unit: "0–3" },
  { v: "chest_pain", l: "Chest Pain Scale (elementi)", unit: "n" },
  { v: "hb", l: "Hb", unit: "g/dL" },
  { v: "ega_ph", l: "EGA pH", unit: "" },
  { v: "ega_pco2", l: "EGA pCO₂", unit: "mmHg" },
  { v: "ega_po2", l: "EGA pO₂", unit: "mmHg" },
  { v: "ega_hco3", l: "EGA HCO₃⁻", unit: "mmol/L" },
  { v: "ega_be", l: "EGA Base Excess", unit: "mmol/L" },
  { v: "ega_sato2", l: "EGA SatO₂", unit: "%" },
  { v: "lattati", l: "Lattati (EGA)", unit: "mmol/L" },
  { v: "custom", l: "Parametro personalizzato", unit: "" },
];

const OPERATORS = [
  { v: ">", l: ">" },
  { v: "≥", l: "≥" },
  { v: "<", l: "<" },
  { v: "≤", l: "≤" },
  { v: "=", l: "=" },
  { v: "!=", l: "diverso da" },
  { v: "between", l: "intervallo (min ↔ max)" },
];

const emptyCondition = () => ({ _cid: Math.random().toString(36).slice(2, 10), parameter: "pa_sys", custom_parameter: "", operator: ">", threshold: "", threshold_max: "" });
const emptyDisc = (order) => ({
  id: Math.random().toString(36).slice(2, 10),
  name: "",
  description: "",
  code: "verde",
  activation_type: "manual",
  parameter: "",
  custom_parameter: "",
  operator: "",
  threshold: null,
  threshold_max: null,
  conditions: [],
  combinator: "AND",
  hint: "",
  notes: "",
  active: true,
  order: order ?? 0,
});
const emptyProto = () => ({
  name: "",
  category: "",
  version: "1.0.0",
  active: true,
  interview_hints: [],
  discriminators: [],
  keywords: [],
  required_exams: [],
  notes: "",
});

// --- Small UI helpers -----------------------------------------------------
const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <Label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</Label>
    {children}
    {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
  </div>
);

// --- Discriminator editor row ---------------------------------------------
function DiscriminatorEditor({ disc, index, total, onChange, onRemove, onDuplicate, onMove, onCopy, highlight, onDragStart, onDragOver, onDrop, isDragging }) {
  const set = (patch) => onChange({ ...disc, ...patch });
  const codeMeta = PRIORITY_CODES.find((c) => c.code === disc.code);
  const isVitalOrLab = ["vital", "lab", "scale"].includes(disc.activation_type);
  const isCombined = disc.activation_type === "combined";
  const rootRef = React.useRef(null);
  useEffect(() => {
    if (highlight && rootRef.current) {
      rootRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight]);
  // Compound parser suggestion (only for still-manual discriminators)
  const suggestion = useMemo(() => {
    if (disc.activation_type && disc.activation_type !== "manual") return null;
    return tryParseCompound(disc.name || "");
  }, [disc.name, disc.activation_type]);

  const applySuggestion = () => {
    if (!suggestion) return;
    set({
      activation_type: "combined",
      combinator: suggestion.combinator,
      conditions: suggestion.conditions,
      parameter: "",
      operator: "",
      threshold: null,
      threshold_max: null,
    });
  };

  return (
    <div
      ref={rootRef}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`border rounded-md p-4 space-y-3 transition-opacity ${
      isDragging ? "opacity-40 border-dashed" :
      highlight ? "border-yellow-400 ring-2 ring-yellow-200 bg-yellow-50" :
      disc.active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"
    }`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="cursor-grab text-slate-400 hover:text-slate-700" title="Trascina per riordinare">
            <DotsSixVertical size={16} weight="bold" />
          </span>
          <span className={`chip-code text-xs ${codeMeta?.classes || "bg-slate-200 text-slate-600"}`}>
            {codeMeta?.label || disc.code}
          </span>
          <span className="text-xs font-mono text-slate-500">#{index + 1}</span>
          {!disc.active && <span className="text-[10px] uppercase tracking-widest text-slate-500">non attivo</span>}
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" data-testid={`disc-up-${index}`} disabled={index === 0} onClick={() => onMove(index, -1)} title="Sposta su">
            <ArrowUp size={16} />
          </Button>
          <Button size="icon" variant="ghost" data-testid={`disc-down-${index}`} disabled={index === total - 1} onClick={() => onMove(index, 1)} title="Sposta giù">
            <ArrowDown size={16} />
          </Button>
          <Button size="icon" variant="ghost" data-testid={`disc-duplicate-${index}`} onClick={onDuplicate} title="Duplica">
            <Copy size={16} />
          </Button>
          <Button size="icon" variant="ghost" data-testid={`disc-copy-${index}`} onClick={onCopy} title="Copia in altri protocolli">
            <CopySimple size={16} />
          </Button>
          <div className="flex items-center gap-2 px-2">
            <Switch checked={!!disc.active} onCheckedChange={(v) => set({ active: v })} data-testid={`disc-active-${index}`} />
          </div>
          <Button size="icon" variant="ghost" data-testid={`disc-delete-${index}`} onClick={onRemove} title="Elimina">
            <Trash size={16} className="text-red-600" />
          </Button>
        </div>
      </div>

      {suggestion && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-sky-50 border border-sky-200 text-sm text-sky-900">
          <Sparkle size={18} weight="fill" className="text-sky-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium">Rilevate {suggestion.conditions.length} condizioni combinabili in AND</div>
            <div className="text-xs text-sky-800 mt-1">
              {suggestion.conditions.map((c, i) => (
                <span key={i} className="inline-block mr-2 font-mono">
                  {c.parameter} {c.operator} {c.threshold}{i < suggestion.conditions.length - 1 ? " ∧" : ""}
                </span>
              ))}
            </div>
          </div>
          <Button size="sm" variant="outline" data-testid={`disc-apply-suggestion-${index}`} onClick={applySuggestion}>
            Trasforma in AND
          </Button>
        </div>
      )}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-6">
          <Field label="Nome discriminatore">
            <Input value={disc.name} onChange={(e) => set({ name: e.target.value })} data-testid={`disc-name-${index}`} />
          </Field>
        </div>
        <div className="col-span-6 md:col-span-3">
          <Field label="Codice priorità">
            <Select value={disc.code} onValueChange={(v) => set({ code: v })}>
              <SelectTrigger data-testid={`disc-code-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITY_CODES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="col-span-6 md:col-span-3">
          <Field label="Tipo di attivazione">
            <Select value={disc.activation_type} onValueChange={(v) => set({ activation_type: v })}>
              <SelectTrigger data-testid={`disc-type-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIVATION_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="col-span-12">
          <Field label="Descrizione">
            <Textarea rows={2} value={disc.description} onChange={(e) => set({ description: e.target.value })} data-testid={`disc-desc-${index}`} />
          </Field>
        </div>
      </div>

      {isVitalOrLab && (
        <div className="grid grid-cols-12 gap-3 p-3 rounded-md bg-slate-50 border border-slate-200">
          <div className="col-span-12 md:col-span-4">
            <Field label="Parametro associato">
              <Select value={disc.parameter} onValueChange={(v) => set({ parameter: v })}>
                <SelectTrigger data-testid={`disc-param-${index}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {PARAMETERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}{p.unit ? ` (${p.unit})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {disc.parameter === "custom" && (
            <div className="col-span-12 md:col-span-4">
              <Field label="Nome parametro personalizzato">
                <Input value={disc.custom_parameter} onChange={(e) => set({ custom_parameter: e.target.value })} data-testid={`disc-customparam-${index}`} />
              </Field>
            </div>
          )}
          <div className="col-span-6 md:col-span-2">
            <Field label="Operatore">
              <Select value={disc.operator} onValueChange={(v) => set({ operator: v })}>
                <SelectTrigger data-testid={`disc-op-${index}`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {OPERATORS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Field label={disc.operator === "between" ? "Soglia minima" : "Valore soglia"}>
              <Input type="number" inputMode="decimal" value={disc.threshold ?? ""} onChange={(e) => set({ threshold: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`disc-threshold-${index}`} />
            </Field>
          </div>
          {disc.operator === "between" && (
            <div className="col-span-6 md:col-span-3">
              <Field label="Soglia massima">
                <Input type="number" inputMode="decimal" value={disc.threshold_max ?? ""} onChange={(e) => set({ threshold_max: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`disc-thresholdmax-${index}`} />
              </Field>
            </div>
          )}
        </div>
      )}

      {isCombined && (
        <div className="p-3 rounded-md bg-slate-50 border border-slate-200 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Condizioni multiple</Label>
            <div className="flex items-center gap-2">
              <div className="flex rounded-md border border-slate-200 overflow-hidden">
                {["AND", "OR"].map((op) => (
                  <button
                    key={op}
                    type="button"
                    data-testid={`disc-combinator-${op.toLowerCase()}-${index}`}
                    onClick={() => set({ combinator: op })}
                    className={`px-3 py-1.5 text-xs font-semibold ${disc.combinator === op ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
                  >{op}</button>
                ))}
              </div>
              <Button size="sm" variant="outline" data-testid={`disc-cond-add-${index}`}
                      onClick={() => set({ conditions: [...(disc.conditions || []), emptyCondition()] })}>
                <Plus size={14} className="mr-1" /> Condizione
              </Button>
            </div>
          </div>
          {(disc.conditions || []).length === 0 && (
            <div className="text-xs text-slate-500 text-center py-3 border-2 border-dashed border-slate-200 rounded-md">
              Aggiungi almeno due condizioni combinate {disc.combinator}.
            </div>
          )}
          {(disc.conditions || []).map((c, ci) => (
            <div key={c._cid ?? `cond-${ci}`} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-4">
                <Select value={c.parameter} onValueChange={(v) => {
                  const arr = [...disc.conditions]; arr[ci] = { ...c, parameter: v }; set({ conditions: arr });
                }}>
                  <SelectTrigger data-testid={`disc-cond-param-${index}-${ci}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PARAMETERS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Select value={c.operator} onValueChange={(v) => {
                  const arr = [...disc.conditions]; arr[ci] = { ...c, operator: v }; set({ conditions: arr });
                }}>
                  <SelectTrigger data-testid={`disc-cond-op-${index}-${ci}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OPERATORS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Input type="number" placeholder="Soglia" value={c.threshold ?? ""}
                       data-testid={`disc-cond-thr-${index}-${ci}`}
                       onChange={(e) => {
                         const arr = [...disc.conditions]; arr[ci] = { ...c, threshold: e.target.value === "" ? null : Number(e.target.value) }; set({ conditions: arr });
                       }} />
              </div>
              {c.operator === "between" && (
                <div className="col-span-2">
                  <Input type="number" placeholder="Max" value={c.threshold_max ?? ""}
                         onChange={(e) => {
                           const arr = [...disc.conditions]; arr[ci] = { ...c, threshold_max: e.target.value === "" ? null : Number(e.target.value) }; set({ conditions: arr });
                         }} />
                </div>
              )}
              <div className={c.operator === "between" ? "col-span-1" : "col-span-3"} />
              <div className="col-span-1 flex justify-end">
                <Button size="icon" variant="ghost" data-testid={`disc-cond-del-${index}-${ci}`}
                        onClick={() => set({ conditions: disc.conditions.filter((_, k) => k !== ci) })}>
                  <Trash size={14} className="text-red-600" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-6">
          <Field label="Suggerimento per l'infermiere">
            <Input value={disc.hint || ""} onChange={(e) => set({ hint: e.target.value })} data-testid={`disc-hint-${index}`} />
          </Field>
        </div>
        <div className="col-span-12 md:col-span-6">
          <Field label="Note">
            <Input value={disc.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid={`disc-notes-${index}`} />
          </Field>
        </div>
      </div>
    </div>
  );
}

// --- Confirmation dialog for new version ---------------------------------
function VersionConfirmDialog({ open, onOpenChange, usage, protocol, prevProtocol, onConfirm }) {
  if (!protocol) return null;
  const prevDiscs = prevProtocol?.discriminators || [];
  const nextDiscs = protocol.discriminators || [];
  const added = nextDiscs.filter((d) => !prevDiscs.find((p) => p.id === d.id));
  const removed = prevDiscs.filter((d) => !nextDiscs.find((p) => p.id === d.id));
  const modified = nextDiscs.filter((d) => {
    const prev = prevDiscs.find((p) => p.id === d.id);
    return prev && JSON.stringify(prev) !== JSON.stringify(d);
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockClockwise size={22} className="text-slate-700" /> Conferma nuova versione
          </DialogTitle>
          <DialogDescription>
            Il protocollo <b>{protocol.name}</b> è già stato utilizzato in {usage?.triages_using_this_version} triage.
            Verrà creata una <b>nuova versione</b>; la versione attuale <span className="font-mono">v.{prevProtocol?.version}</span> resterà associata ai triage esistenti.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
              <div className="text-xs uppercase tracking-widest text-emerald-700">Aggiunti</div>
              <div className="font-mono text-2xl">{added.length}</div>
            </div>
            <div className="p-3 rounded-md bg-orange-50 border border-orange-200">
              <div className="text-xs uppercase tracking-widest text-orange-700">Modificati</div>
              <div className="font-mono text-2xl">{modified.length}</div>
            </div>
            <div className="p-3 rounded-md bg-red-50 border border-red-200">
              <div className="text-xs uppercase tracking-widest text-red-700">Rimossi</div>
              <div className="font-mono text-2xl">{removed.length}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="uppercase tracking-widest text-slate-500 mb-1">Versione precedente</div>
              <div className="font-mono">v.{prevProtocol?.version}</div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-slate-500 mb-1">Nuova versione (proposta)</div>
              <div className="font-mono">v.{protocol.version}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 flex gap-2">
            <Warning size={14} className="text-orange-600 shrink-0 mt-0.5" />
            <span>Prima di rendere attiva la nuova versione, verifica con attenzione le modifiche. L'operazione è tracciata nel registro audit.</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={onConfirm} data-testid="confirm-new-version" className="bg-slate-900 hover:bg-slate-800">
            Attiva nuova versione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- CSV import dialog ---------------------------------------------------
function CsvImportDialog({ open, onOpenChange, protocols, onImport }) {
  const [parsed, setParsed] = useState(null);
  const [errors, setErrors] = useState([]);
  const [target, setTarget] = useState({}); // proto name -> protocol_id

  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    try {
      const { data } = await api.post("/discriminators/parse-csv", text, { headers: { "Content-Type": "text/csv" } });
      setParsed(data.grouped);
      setErrors(data.errors || []);
      // Auto-map proto name -> existing protocol
      const map = {};
      Object.keys(data.grouped || {}).forEach((pn) => {
        const found = protocols.find((p) => p.name.toLowerCase() === pn.toLowerCase());
        if (found) map[pn] = found.protocol_id;
      });
      setTarget(map);
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV non valido");
    }
    e.target.value = "";
  };

  const doImport = () => {
    const changes = [];
    Object.entries(parsed || {}).forEach(([pn, discs]) => {
      const pid = target[pn];
      if (!pid) return;
      changes.push({ protocol_id: pid, discriminators: discs });
    });
    onImport(changes);
    setParsed(null); setErrors([]); setTarget({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCsv size={22} /> Importa discriminatori da CSV
          </DialogTitle>
          <DialogDescription>
            Colonne attese: <span className="font-mono">Protocollo | Discriminatore | Descrizione | Codice | Tipo regola | Parametro | Operatore | Soglia | Note</span>.
            Separatori supportati: virgola, punto e virgola, tab.
          </DialogDescription>
        </DialogHeader>
        {!parsed && (
          <label className="cursor-pointer block border-2 border-dashed border-slate-300 rounded-md p-8 text-center hover:bg-slate-50">
            <UploadSimple size={32} className="mx-auto text-slate-400 mb-2" />
            <div className="text-sm font-medium">Seleziona un file .csv</div>
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="csv-file-input" />
          </label>
        )}
        {parsed && (
          <div className="space-y-3">
            {errors.length > 0 && (
              <div className="p-3 rounded-md bg-orange-50 border border-orange-200 text-xs text-orange-800">
                <div className="font-semibold mb-1">{errors.length} avvisi:</div>
                <ul className="list-disc list-inside">
                  {errors.slice(0, 5).map((e, i) => <li key={i}>Riga {e.row}: {e.reason}</li>)}
                </ul>
              </div>
            )}
            {Object.entries(parsed).map(([pn, discs]) => (
              <div key={pn} className="border border-slate-200 rounded-md p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="font-medium">{pn}</div>
                  <span className="text-xs font-mono text-slate-500">{discs.length} discriminatori</span>
                </div>
                <div className="text-xs text-slate-500 mb-2">Mappa su protocollo esistente:</div>
                <Select value={target[pn] || ""} onValueChange={(v) => setTarget({ ...target, [pn]: v })}>
                  <SelectTrigger data-testid={`csv-map-${pn}`}><SelectValue placeholder="Seleziona protocollo di destinazione" /></SelectTrigger>
                  <SelectContent>
                    {protocols.map((p) => <SelectItem key={p.protocol_id} value={p.protocol_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => { setParsed(null); setErrors([]); onOpenChange(false); }}>Annulla</Button>
          <Button onClick={doImport} disabled={!parsed || Object.keys(target).length === 0} data-testid="csv-import-confirm">
            Importa nei protocolli selezionati
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Main page -----------------------------------------------------------
export default function AdminPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [prevSnapshot, setPrevSnapshot] = useState(null);
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [versionDialog, setVersionDialog] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // {parsed, preview}
  const [historyProtocolId, setHistoryProtocolId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightDiscId, setHighlightDiscId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [copyDisc, setCopyDisc] = useState(null);

  const load = async () => setItems((await api.get("/protocols")).data);
  useEffect(() => { load(); }, []);

  const startEdit = async (p) => {
    const full = (await api.get(`/protocols/${p.protocol_id}`)).data;
    const use = (await api.get(`/protocols/${p.protocol_id}/usage`)).data;
    setEditing(JSON.parse(JSON.stringify(full)));
    setPrevSnapshot(JSON.parse(JSON.stringify(full)));
    setUsage(use);
    setOpen(true);
  };
  const startNew = () => { setEditing(emptyProto()); setPrevSnapshot(null); setUsage(null); setOpen(true); };

  const setDiscs = (fn) => setEditing((cur) => ({ ...cur, discriminators: fn(cur.discriminators || []) }));

  const addDisc = () => setDiscs((arr) => [...arr, emptyDisc(arr.length)]);
  const updateDisc = (i, next) => setDiscs((arr) => { const c = [...arr]; c[i] = next; return c; });
  const removeDisc = (i) => setDiscs((arr) => arr.filter((_, k) => k !== i).map((d, k) => ({ ...d, order: k })));
  const duplicateDisc = (i) => setDiscs((arr) => {
    const src = arr[i]; const clone = { ...JSON.parse(JSON.stringify(src)), id: Math.random().toString(36).slice(2, 10), name: src.name + " (copia)" };
    const c = [...arr]; c.splice(i + 1, 0, clone); return c.map((d, k) => ({ ...d, order: k }));
  });
  const moveDisc = (i, dir) => setDiscs((arr) => {
    const j = i + dir; if (j < 0 || j >= arr.length) return arr;
    const c = [...arr]; [c[i], c[j]] = [c[j], c[i]]; return c.map((d, k) => ({ ...d, order: k }));
  });

  const doSave = async ({ forceNewVersion = false } = {}) => {
    try {
      let saved;
      if (editing.protocol_id) {
        const url = `/protocols/${editing.protocol_id}` + (forceNewVersion ? "?force_new_version=true" : "");
        const { data } = await api.put(url, editing);
        saved = data;
        toast.success(forceNewVersion || (usage?.requires_new_version)
          ? `Nuova versione ${saved.version} attivata`
          : "Protocollo aggiornato");
      } else {
        const { data } = await api.post("/protocols", editing);
        saved = data;
        toast.success("Protocollo creato");
      }
      setOpen(false); setVersionDialog(false); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il salvataggio");
    }
  };

  const trySave = () => {
    if (editing.protocol_id && usage?.requires_new_version) {
      // Ensure version bumps
      if (editing.version === prevSnapshot?.version) {
        const parts = (editing.version || "1.0.0").split(".");
        parts[2] = String((parseInt(parts[2] || "0", 10) || 0) + 1);
        setEditing((cur) => ({ ...cur, version: parts.slice(0, 3).join(".") }));
      }
      setVersionDialog(true);
      return;
    }
    doSave();
  };

  const remove = async (id) => {
    if (!window.confirm("Disattivare il protocollo?")) return;
    await api.delete(`/protocols/${id}`);
    await load();
    toast.success("Protocollo disattivato");
  };

  const exportAll = async () => {
    const { data } = await api.get("/protocols/export/all");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `tiger-protocols-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const importJson = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const { data } = await api.post("/protocols/import?dry_run=true", parsed);
      setImportPreview({ parsed, preview: data });
    } catch { toast.error("File JSON non valido"); }
    e.target.value = "";
  };

  const confirmImportJson = async () => {
    if (!importPreview) return;
    try {
      const res = await api.post("/protocols/import", importPreview.parsed);
      toast.success(`Importati ${res.data.imported} protocolli`);
      setImportPreview(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const applyCsvImport = async (changes) => {
    for (const c of changes) {
      const full = (await api.get(`/protocols/${c.protocol_id}`)).data;
      const use = (await api.get(`/protocols/${c.protocol_id}/usage`)).data;
      const startOrder = (full.discriminators || []).length;
      const merged = [
        ...(full.discriminators || []),
        ...c.discriminators.map((d, i) => ({ ...d, order: startOrder + i })),
      ];
      const url = `/protocols/${c.protocol_id}` + (use.requires_new_version ? "?force_new_version=true" : "");
      await api.put(url, { ...full, discriminators: merged });
    }
    toast.success(`Importati discriminatori in ${changes.length} protocolli`);
    await load();
  };

  const sortedDiscs = useMemo(() => {
    if (!editing) return [];
    return [...(editing.discriminators || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [editing]);

  return (
    <div>
      <div className="border-b border-slate-200 bg-white px-6 py-4 flex items-center justify-between">
        <div>
          <div className="font-heading text-xl font-semibold">Gestione protocolli e discriminatori</div>
          <div className="text-xs text-slate-500">Nessun criterio clinico viene inventato. I valori sono forniti dall'amministratore.</div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button variant="outline" onClick={() => setSearchOpen(true)} data-testid="global-search-button" className="min-h-[44px]">
            <MagnifyingGlass size={16} className="mr-2" /> Cerca discriminatore
          </Button>
          <Button variant="outline" onClick={() => setCsvOpen(true)} data-testid="csv-import-button" className="min-h-[44px]">
            <FileCsv size={16} className="mr-2" /> Importa discriminatori (CSV)
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".json" className="hidden" onChange={importJson} data-testid="import-json-input" />
            <span className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium">
              <UploadSimple size={16} /> Importa JSON
            </span>
          </label>
          <Button variant="outline" onClick={exportAll} data-testid="export-json-button" className="min-h-[44px]">
            <DownloadSimple size={16} className="mr-2" /> Esporta
          </Button>
          <Button onClick={startNew} data-testid="new-protocol-button" className="min-h-[44px] bg-slate-900 hover:bg-slate-800">
            <Plus size={16} className="mr-2" /> Nuovo protocollo
          </Button>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Nome</th>
                <th className="text-left p-3">Categoria</th>
                <th className="text-left p-3">Versione</th>
                <th className="text-left p-3">Discriminatori</th>
                <th className="text-left p-3">Ultima modifica</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.protocol_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-slate-600">{p.category || "—"}</td>
                  <td className="p-3 font-mono text-xs">{p.version}</td>
                  <td className="p-3 font-mono text-xs">{(p.discriminators || []).filter((d) => d.active !== false).length} / {(p.discriminators || []).length}</td>
                  <td className="p-3 text-xs text-slate-500">{(p.last_modified || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3 flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setHistoryProtocolId(p.protocol_id)} data-testid={`history-${p.protocol_id}`} title="Storico versioni">
                      <ClockCounterClockwise size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => startEdit(p)} data-testid={`edit-${p.protocol_id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p.protocol_id)} data-testid={`delete-${p.protocol_id}`}>
                      <Trash size={16} className="text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Editor Dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setHighlightDiscId(null); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {editing?.protocol_id ? "Modifica protocollo" : "Nuovo protocollo"}
              {usage?.requires_new_version && (
                <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-md bg-orange-100 text-orange-800 border border-orange-200">
                  usato in {usage.triages_using_this_version} triage — richiesta nuova versione
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <Tabs value={highlightDiscId ? "discs" : undefined} defaultValue="header" className="mt-2">
              <TabsList className="grid grid-cols-3">
                <TabsTrigger value="header" data-testid="tab-header">Intestazione</TabsTrigger>
                <TabsTrigger value="discs" data-testid="tab-discs">Discriminatori ({(editing.discriminators || []).length})</TabsTrigger>
                <TabsTrigger value="hints" data-testid="tab-hints">Suggerimenti intervista</TabsTrigger>
                <TabsTrigger value="keywords" data-testid="tab-keywords">Parole chiave ({(editing.keywords || []).length})</TabsTrigger>
                <TabsTrigger value="exams" data-testid="tab-exams">Esami richiesti ({(editing.required_exams || []).length})</TabsTrigger>
              </TabsList>
              <TabsContent value="header" className="pt-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Field label="Nome protocollo">
                      <Input data-testid="proto-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Versione">
                    <Input data-testid="proto-version" value={editing.version} onChange={(e) => setEditing({ ...editing, version: e.target.value })} />
                  </Field>
                  <div className="col-span-3">
                    <Field label="Categoria">
                      <Input data-testid="proto-category" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                    </Field>
                  </div>
                  <div className="col-span-3">
                    <Field label="Note protocollo">
                      <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="proto-notes" />
                    </Field>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="discs" className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-slate-500">
                    Trascina i discriminatori (icona <b>⋮⋮</b>) per riordinarli all'interno del proprio codice. Le soglie devono essere fornite dall'operatore.
                  </div>
                  <Button size="sm" variant="outline" onClick={addDisc} data-testid="add-disc">
                    <Plus size={14} className="mr-1" /> Aggiungi discriminatore
                  </Button>
                </div>
                <div className="space-y-4">
                  {PRIORITY_CODES.map((c) => {
                    const groupDiscs = sortedDiscs.filter((d) => d.code === c.code);
                    if (groupDiscs.length === 0) return null;
                    return (
                      <section key={c.code} className="rounded-md border border-slate-200" data-testid={`admin-group-${c.code}`}>
                        <header className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
                          <span className={`chip-code text-xs ${c.classes}`}>{c.label}</span>
                          <span className="text-xs font-mono text-slate-500">{groupDiscs.length} discriminatori</span>
                        </header>
                        <div
                          className="p-3 space-y-3"
                          onDragOver={(e) => e.preventDefault()}
                        >
                          {groupDiscs.map((d) => {
                            const i = sortedDiscs.findIndex((x) => x.id === d.id);
                            return (
                              <DiscriminatorEditor
                                key={d.id}
                                disc={d}
                                index={i}
                                total={sortedDiscs.length}
                                highlight={highlightDiscId === d.id}
                                isDragging={dragId === d.id}
                                onDragStart={(e) => {
                                  setDragId(d.id);
                                  e.dataTransfer.effectAllowed = "move";
                                  e.dataTransfer.setData("text/plain", d.id);
                                }}
                                onDragOver={(e) => {
                                  if (!dragId || dragId === d.id) return;
                                  const src = sortedDiscs.find((x) => x.id === dragId);
                                  if (!src || src.code !== d.code) return;
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = "move";
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  if (!dragId || dragId === d.id) { setDragId(null); return; }
                                  setDiscs((arr) => {
                                    const list = [...arr];
                                    const srcIdx = list.findIndex((x) => x.id === dragId);
                                    const dstIdx = list.findIndex((x) => x.id === d.id);
                                    if (srcIdx === -1 || dstIdx === -1) return arr;
                                    if (list[srcIdx].code !== list[dstIdx].code) return arr;
                                    const [moved] = list.splice(srcIdx, 1);
                                    const finalDst = list.findIndex((x) => x.id === d.id);
                                    list.splice(finalDst, 0, moved);
                                    return list.map((x, k) => ({ ...x, order: k }));
                                  });
                                  setDragId(null);
                                }}
                                onChange={(next) => updateDisc(i, next)}
                                onRemove={() => removeDisc(i)}
                                onDuplicate={() => duplicateDisc(i)}
                                onCopy={() => setCopyDisc(d)}
                                onMove={moveDisc}
                              />
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                  {sortedDiscs.length === 0 && (
                    <div className="text-sm text-slate-500 text-center py-10 border-2 border-dashed border-slate-200 rounded-md">
                      Nessun discriminatore. Aggiungerli manualmente oppure importarli da CSV.
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="hints" className="pt-4">
                <Field label="Suggerimenti per l'intervista di triage (uno per riga)">
                  <Textarea rows={8} value={(editing.interview_hints || []).join("\n")}
                            data-testid="proto-hints"
                            onChange={(e) => setEditing({ ...editing, interview_hints: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} />
                </Field>
              </TabsContent>
              <TabsContent value="keywords" className="pt-4 space-y-2">
                <Field
                  label="Parole chiave e sinonimi (uno per riga)"
                  hint="Termini colloquiali/medici alternativi che devono richiamare questo protocollo nella ricerca. Es. per Perdita di coscienza: sincope, svenimento, lipotimia."
                >
                  <Textarea
                    rows={10}
                    data-testid="proto-keywords"
                    value={(editing.keywords || []).join("\n")}
                    onChange={(e) => setEditing({
                      ...editing,
                      keywords: e.target.value
                        .split("\n")
                        .map((s) => s.toLowerCase().trim())
                        .filter(Boolean),
                    })}
                    placeholder="sincope&#10;svenimento&#10;lipotimia&#10;mancamento"
                  />
                </Field>
                {(editing.keywords || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {editing.keywords.map((k) => (
                      <span key={`kw-${k}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="exams" className="pt-4 space-y-3">
                <div className="text-xs text-slate-500 -mb-1">
                  Per ogni esame, scegli se e <b>obbligatorio</b>, <b>suggerito</b> oppure non richiesto per questo protocollo.
                </div>
                {[
                  {code:"EGA_A",   label:"EGA arterioso"},
                  {code:"EGA_V",   label:"EGA venoso"},
                  {code:"ECG",     label:"ECG 12 derivazioni"},
                  {code:"GLIC",    label:"Glicemia capillare"},
                  {code:"MONITOR", label:"Monitor continuo"},
                  {code:"TROPO",   label:"Troponina rapida"},
                  {code:"HCG",     label:"β-HCG"},
                  {code:"URINE",   label:"Stick urine"},
                ].map((ex) => {
                  const current = (editing.required_exams || []).find((x) => x.code === ex.code);
                  const value = current?.urgency || "none";
                  const setUrg = (v) => {
                    setEditing((cur) => {
                      const others = (cur.required_exams || []).filter((x) => x.code !== ex.code);
                      const next = v === "none" ? others : [...others, {code: ex.code, urgency: v}];
                      return { ...cur, required_exams: next };
                    });
                  };
                  return (
                    <div key={ex.code} data-testid={`exam-row-${ex.code}`}
                         className="flex items-center justify-between gap-2 px-3 py-2 border border-slate-200 rounded-md bg-white">
                      <div className="text-sm font-medium">{ex.label}
                        <span className="ml-2 text-[10px] font-mono text-slate-400">{ex.code}</span>
                      </div>
                      <div className="inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50">
                        {[
                          {v:"none",         l:"—",       tone:"text-slate-500"},
                          {v:"suggerito",    l:"Suggerito", tone:"text-amber-700"},
                          {v:"obbligatorio", l:"Obbligatorio", tone:"text-red-700"},
                        ].map((opt) => (
                          <button
                            key={opt.v}
                            type="button"
                            data-testid={`exam-${ex.code}-${opt.v}`}
                            onClick={() => setUrg(opt.v)}
                            className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                              value === opt.v ? "bg-white shadow-sm " + opt.tone : "text-slate-400 hover:text-slate-700"
                            }`}
                          >{opt.l}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={trySave} data-testid="save-protocol" className="bg-slate-900 hover:bg-slate-800">
              {usage?.requires_new_version ? "Salva e crea nuova versione" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VersionConfirmDialog
        open={versionDialog}
        onOpenChange={setVersionDialog}
        usage={usage}
        protocol={editing}
        prevProtocol={prevSnapshot}
        onConfirm={() => doSave({ forceNewVersion: true })}
      />

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        protocols={items}
        onImport={applyCsvImport}
      />

      <VersionHistoryDialog
        open={!!historyProtocolId}
        onOpenChange={(v) => { if (!v) setHistoryProtocolId(null); }}
        protocolId={historyProtocolId}
      />

      <DiscriminatorSearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onOpenProtocol={async (pid, discId) => {
          const p = items.find((x) => x.protocol_id === pid) || (await api.get(`/protocols/${pid}`)).data;
          setHighlightDiscId(discId);
          startEdit(p);
        }}
      />

      <CopyDiscriminatorDialog
        open={!!copyDisc}
        onOpenChange={(v) => { if (!v) setCopyDisc(null); }}
        discriminator={copyDisc}
        sourceProtocolId={editing?.protocol_id}
        protocols={items}
        onCopied={load}
      />

      {/* JSON import preview dialog */}
      <Dialog open={!!importPreview} onOpenChange={(v) => { if (!v) setImportPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UploadSimple size={22} /> Anteprima importazione JSON
            </DialogTitle>
            <DialogDescription>
              Verifica cosa verrà creato o aggiornato prima di confermare. Nessuna modifica è stata salvata.
            </DialogDescription>
          </DialogHeader>
          {importPreview?.preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                  <div className="text-xs uppercase tracking-widest text-emerald-700">Nuovi</div>
                  <div className="font-mono text-2xl" data-testid="preview-insert-count">{importPreview.preview.would_insert.length}</div>
                </div>
                <div className="p-3 rounded-md bg-orange-50 border border-orange-200">
                  <div className="text-xs uppercase tracking-widest text-orange-700">Aggiornati</div>
                  <div className="font-mono text-2xl" data-testid="preview-update-count">{importPreview.preview.would_update.length}</div>
                </div>
                <div className="p-3 rounded-md bg-red-50 border border-red-200">
                  <div className="text-xs uppercase tracking-widest text-red-700">Errori</div>
                  <div className="font-mono text-2xl">{importPreview.preview.errors.length}</div>
                </div>
              </div>
              {importPreview.preview.would_insert.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Nuovi protocolli</div>
                  <ul className="text-sm space-y-1 max-h-32 overflow-auto border border-slate-200 rounded-md p-2 bg-slate-50">
                    {importPreview.preview.would_insert.map((s, i) => <li key={i}>+ {s}</li>)}
                  </ul>
                </div>
              )}
              {importPreview.preview.would_update.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Aggiornamenti</div>
                  <ul className="text-sm space-y-1 max-h-32 overflow-auto border border-slate-200 rounded-md p-2 bg-slate-50">
                    {importPreview.preview.would_update.map((s, i) => <li key={i}>~ {s}</li>)}
                  </ul>
                </div>
              )}
              {importPreview.preview.errors.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-red-700 mb-1">Errori</div>
                  <ul className="text-sm space-y-1 text-red-700">
                    {importPreview.preview.errors.map((e, i) => <li key={i}>! {e.reason}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportPreview(null)}>Annulla</Button>
            <Button onClick={confirmImportJson} data-testid="confirm-import-json" className="bg-slate-900 hover:bg-slate-800">
              Conferma importazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
